import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import FormHeader from "@/components/ui/organisms/MHeader";
import { MButton } from "@/components/ui/atoms/MButtons";
import MInputField from "@/components/ui/atoms/MInputField";
import MDateInputField from "@/components/ui/molecules/MDateInputField";
import { toLocalDateString } from "@/utils/date-utils";
import AutoCompleteInput from "@/components/ui/organisms/AutoCompleteInput";
import { getMemberInfo } from "@/hooks/useMembers";
import { useInsurance } from "@/hooks/useInsurance";
import { useTypeAhead } from "@/hooks/useTypeAhead";
import { useToast } from "@/components/ui/atoms/MToastProvider";
import MSpacer from "@/components/ui/atoms/MSpacer";
import { useTranslation } from "react-i18next";
import { trackUserDataLoaded } from '@/utils/gtm';
import { ANALYTICS_SCREEN_TYPES, getInsuranceScreenName } from '@/constants/analytics.constants';

const InsuranceForm: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const location = useLocation();
  const navigate = useNavigate();
  const memberInfo = getMemberInfo();
  const { createInsurance, updateInsurance, loading, error } = useInsurance({
    memberInfo,
  });
  const {
    values: insuranceCompanies,
    loading: insuranceLoading,
    fetch: fetchInsuranceCompanies,
  } = useTypeAhead("insurance_private");
  const {
    values: govtSchemes,
    loading: govtLoading,
    fetch: fetchGovtSchemes,
  } = useTypeAhead("insurance_govt");

  // Route state: insuranceType (for add), insurance (for edit)
  const editInsurance = location.state?.insurance || null;
  const insuranceType =
    location.state?.insuranceType || editInsurance?.insuranceType || "private";
  const providerLabel =
    insuranceType === "government"
      ? "Which panel or scheme is it? (eg: CGHS)"
      : "Which company issued your policy? (e.g., ICICI Lombard)";

  // Form state
  const [provider, setProvider] = useState(editInsurance?.insurerName || "");
  const [policyNumber, setPolicyNumber] = useState(
    editInsurance?.policyNumber || ""
  );
  const [primaryPolicyHolder, setPrimaryPolicyHolder] = useState(
    editInsurance?.primaryPolicyHolderName ||
      [memberInfo.firstName, memberInfo.middleName, memberInfo.lastName]
        .filter(Boolean)
        .join(" ")
  );
  const [isLifetimePolicy, setIsLifetimePolicy] = useState(
    editInsurance?.isLifetimePolicy || false
  );
  const [policyEndDate, setPolicyEndDate] = useState(
    editInsurance?.policyEndDate || editInsurance?.validUntil || ""
  );
  const [saving, setSaving] = useState(false);

  // Validation state
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Store original values for edit mode comparison
  const original = React.useRef({
    provider: editInsurance?.insurerName || "",
    policyNumber: editInsurance?.policyNumber || "",
    primaryPolicyHolder:
      editInsurance?.primaryPolicyHolderName ||
      [memberInfo.firstName, memberInfo.middleName, memberInfo.lastName]
        .filter(Boolean)
        .join(" "),
    isLifetimePolicy: editInsurance?.isLifetimePolicy || false,
    policyEndDate:
      editInsurance?.policyEndDate || editInsurance?.validUntil || "",
  });

  // Filtered insurance companies for autocomplete
  const [filteredCompanies, setFilteredCompanies] = useState<string[]>([]);

  // Fetch insurance companies on mount
  useEffect(() => {
    if (insuranceType === "private") {
      fetchInsuranceCompanies();
    } else if (insuranceType === "government") {
      fetchGovtSchemes();
    }
  }, [insuranceType, fetchInsuranceCompanies, fetchGovtSchemes]);

  // Update filtered companies when insurance companies data changes
  useEffect(() => {
    if (insuranceType === "private") {
      setFilteredCompanies([...insuranceCompanies].sort((a, b) => a.localeCompare(b)));
    } else if (insuranceType === "government") {
      setFilteredCompanies([...govtSchemes].sort((a, b) => a.localeCompare(b)));
    }
  }, [insuranceType, insuranceCompanies, govtSchemes]);

  // Handle search for insurance companies
  const handleInsuranceSearch = (query: string) => {
    const source =
      insuranceType === "private" ? insuranceCompanies : govtSchemes;
    if (!query.trim()) {
      setFilteredCompanies([...source].sort((a, b) => a.localeCompare(b)));
      return;
    }
    const filtered = source.filter((company) =>
      company.toLowerCase().includes(query.toLowerCase())
    );
    setFilteredCompanies([...filtered].sort((a, b) => a.localeCompare(b)));
  };

  // Only enable Done when all required fields are filled
  // For lifetime policies, policyEndDate is not required
  const isValid =
    provider.trim() &&
    policyNumber.trim() &&
    primaryPolicyHolder.trim() &&
    (isLifetimePolicy || policyEndDate.trim());

  // In edit mode, only enable if a value changed; in add mode, enable if all fields filled
  const isChanged = editInsurance
    ? provider !== original.current.provider ||
      policyNumber !== original.current.policyNumber ||
      primaryPolicyHolder !== original.current.primaryPolicyHolder ||
      isLifetimePolicy !== original.current.isLifetimePolicy ||
      policyEndDate !== original.current.policyEndDate
    : isValid;

  const handleBack = () => navigate(-1);

  const handleSave = async () => {
    // Validate fields
    const newErrors: { [key: string]: string } = {};
    if (!provider.trim()) newErrors.provider = "Required";
    if (!policyNumber.trim()) newErrors.policyNumber = "Required";
    if (!primaryPolicyHolder.trim()) newErrors.primaryPolicyHolder = "Required";
    // Policy end date is only required if not a lifetime policy
    if (!isLifetimePolicy && !policyEndDate.trim()) newErrors.policyEndDate = "Required";
    setErrors(newErrors);
    if (Object.keys(newErrors).length > 0) return;

    setSaving(true);
    try {
      const values: any = {
        memberId: memberInfo.id,
        documentId: editInsurance?.documentId || "",
        familyId: (memberInfo as any).current_family,
        insuranceType,
        insurerName: provider,
        policyNumber,
        primaryPolicyHolderName: primaryPolicyHolder,
        ...(memberInfo.memberType === "p" ? { memberType: "p" } : {}),
      };
      
      // Only add isLifetimePolicy for government insurance
      if (insuranceType === "government") {
        values.isLifetimePolicy = isLifetimePolicy;
      }
      
      // Only add policyEndDate if not a lifetime policy
      if (!isLifetimePolicy) {
        values.policyEndDate = policyEndDate;
      }
      
      if (editInsurance) {
        await updateInsurance(editInsurance.id || editInsurance._id, values);
      } else {
        await createInsurance(values);
      }
      
      // Track completion event with policy provider
      trackUserDataLoaded({
        memberInfo,
        screenType: ANALYTICS_SCREEN_TYPES.USER_PROFILE,
        screenName: getInsuranceScreenName(insuranceType as 'private' | 'government' | 'family', 'add_complete'),
        additionalPageData: {
          policy_provider: provider,
        },
      });

      navigate("/profile/basic-information/insurance");
    } catch (err: any) {
      showToast({
        message: t("errors.toast"),
        variant: "error",
      });
    } finally {
      setSaving(false);
    }
  };

  // Set minDate to today and maxDate to 20 years from today
  const today = new Date();
  const minDate = today;
  const maxDate = new Date(
    today.getFullYear() + 20,
    today.getMonth(),
    today.getDate()
  );

  return (
    <div className="profile-page-root flex flex-col" style={{ minHeight: 'calc(var(--vh, 1vh) * 100 - 128px)' }}>
      <FormHeader module="insurance_form" memberInfo={memberInfo} />
      <MSpacer />
      <form className="profile-input-container flex-1">
        <AutoCompleteInput
          label={t(`insurance_form.provider.${insuranceType}`)}
          data={filteredCompanies.map((company) => ({ name: company }))}
          getOptionLabel={(item) => item.name}
          onSelect={(item) => {
            const selectedCompany = typeof item === "string" ? item : item.name;
            setProvider(selectedCompany);
          }}
          value={provider}
          onChange={setProvider}
          onSearch={handleInsuranceSearch}
          loading={insuranceType === "private" ? insuranceLoading : govtLoading}
          placeholder={t("common.enterOrChoose")}
          allowCustomInput={true}
          minLength={3}
          className="w-full"
          showAllOnFocus={true}
        />
        <MInputField
          value={policyNumber}
          onChange={(e) => setPolicyNumber(e.target.value)}
          placeholder={t("common.notAnswered")}
          size="md"
          variant="Underlined"
          state={errors.policyNumber ? "Error" : undefined}
          stateMessage={errors.policyNumber || ""}
          label={t("insurance_form.policyNumber")}
        />
        <MInputField
          value={primaryPolicyHolder}
          onChange={(e) => setPrimaryPolicyHolder(e.target.value)}
          placeholder={t("common.notAnswered")}
          size="md"
          variant="Underlined"
          state={errors.primaryPolicyHolder ? "Error" : undefined}
          stateMessage={errors.primaryPolicyHolder || ""}
          label={t("insurance_form.primaryPolicyHolder")}
        />
        {insuranceType === "government" && (
          <div className="flex flex-col gap-3 w-full ">
            <label className="text-base font-medium" style={{ color: '#4C189A' }}>
              {t("insurance_form.isLifetimePolicy")}
            </label>
            <div className="flex gap-3">
              <MButton
                type="button"
                variant={isLifetimePolicy ? "filled" : "outline"}
                size="md"
                className="flex-1"
                onClick={() => {
                  setIsLifetimePolicy(true);
                  setPolicyEndDate("");
                }}
                text={t("common.yes")}
              />
              <MButton
                type="button"
                variant={!isLifetimePolicy ? "filled" : "outline"}
                size="md"
                className="flex-1"
                onClick={() => setIsLifetimePolicy(false)}
                text={t("common.no")}
              />
            </div>
          </div>
        )}
        {!isLifetimePolicy && (
          <MDateInputField
            value={policyEndDate ? new Date(policyEndDate) : undefined}
            onDateChange={(date) =>
              setPolicyEndDate(date ? toLocalDateString(date) : "")
            }
            placeholder={t("common.notAnswered")}
            size="md"
            variant="Underlined"
            state={errors.policyEndDate ? "Error" : undefined}
            stateMessage={errors.policyEndDate || ""}
            label={t("insurance_form.policyEndDate")}
            minDate={minDate}
            maxDate={maxDate}
          />
        )}
      </form>
      <div className="confirm-footer">
        <MButton
          type="button"
          variant="filled"
          size="lg"
          className="w-full"
          disabled={!isChanged || !isValid || saving || loading}
          onClick={handleSave}
          text={t("common.done")}
        />
      </div>
    </div>
  );
};

export default InsuranceForm;
